import { DB_QUERY_COMMANDS, quoteIdentFor } from '../../extensions/dbEngines';
import { cellToText } from '../dbCellText';
import { MAX_RENDER_ROWS } from '../dbGridModel';

// Sidebar-armed pendingActions older than this are dropped instead of fired
// (generous for a first page load, short enough that a reconnect minutes
// later can't trigger a surprise export modal / file picker).
export const PENDING_ACTION_TTL_MS = 30_000;

// ---- Engine helpers ---------------------------------------------------------
// A DB Manager tab carries the engine it was opened for; everything below
// (identifier quoting, placeholder style, PK discovery) dispatches on it.
// Command names, dialect quirks, and the schema catalog live in the shared
// dbEngines module (also feeds SQL completions).

export function engineOf(tab) {
  return DB_QUERY_COMMANDS[tab?.engine] ? tab.engine : 'mysql';
}

// "db"."table" for server engines; a bare "table" for SQLite (its dbName is
// the implicit "main" schema and query tabs may carry no dbName at all).
export function tableRefFor(engine, tab) {
  if (engine === 'sqlite' || !tab.dbName) return quoteIdentFor(engine, tab.tableName);
  return `${quoteIdentFor(engine, tab.dbName)}.${quoteIdentFor(engine, tab.tableName)}`;
}

// The UPDATE/INSERT/DELETE statement builders (row-addressing WHERE, postgres
// placeholder casts) live in dbGridSql.js so a plain node harness can
// exercise them.

// Mirrors SyntaxHighlightInput's resolveDynSuggestionState: looks at the
// identifier prefix immediately before the caret and filters known column
// names by it. Excludes an exact match so the dropdown doesn't re-trigger
// right after a suggestion has just been inserted.
export function resolveColumnSuggestionState(textBefore, cursorPos, columns) {
  const match = textBefore.match(/([A-Za-z_][A-Za-z0-9_]*)$/);
  if (!match) return null;
  const prefix = match[1];
  const lowerPrefix = prefix.toLowerCase();
  const items = columns.filter((c) => c.name.toLowerCase().startsWith(lowerPrefix) && c.name.toLowerCase() !== lowerPrefix);
  if (!items.length) return null;
  return { items, insertStart: cursorPos - prefix.length, insertEnd: cursorPos };
}

// Per-tab page size for table browsing (persisted on the tab); 100 stays the
// default. The CSV/JSON/INSERT serializers live in dbExportUtils (shared with
// the file-export modal).
export const PAGE_SIZE_OPTIONS = [100, 200, 500, 1000];

// Row caps offered on a QUERY tab. Table browse pages through a table it can
// count; a query tab sends whatever SQL the user typed, so this is the only
// thing standing between `SELECT * FROM events` and pulling the whole table
// into memory. DEFAULT_QUERY_MAX_ROWS stays the default because it is what the
// backend already enforced silently — this makes it visible and adjustable
// rather than changing the behaviour out from under anyone.
export const QUERY_MAX_ROWS_OPTIONS = [100, 1000, 10000, 50000, 200000];

export const DEFAULT_PAGE_SIZE = 100;

export function pageSizeOf(tab) {
  // A RANGE CHECK, not a membership test. The picker accepts any typed number,
  // and the old `PAGE_SIZE_OPTIONS.includes(...)` silently reverted anything
  // off-list to the default — so a custom 250 looked saved and then was not.
  //
  // Upper bound is MAX_RENDER_ROWS because the grid draws no more than that:
  // a larger page would fetch rows nobody can see.
  const n = Number(tab?.pageSize);
  if (!Number.isFinite(n)) return DEFAULT_PAGE_SIZE;
  return Math.min(MAX_RENDER_ROWS, Math.max(1, Math.round(n)));
}

// The grid renders plain <tr>s with no virtualization — a query tab result of
// hundreds of thousands of rows would lock the DOM. Render at most this many
// rows and point at Export for the rest (the full result set stays in memory
// for Copy/Export).
/**
 * Width key for the row-number column. NUL-prefixed because it shares a map
 * with real column names and no SQL identifier can contain one.
 *
 * It has to be in the map at all because `table-layout: fixed` hands an
 * unsized <col> whatever space is left over — and once every data column is
 * explicitly sized, that is nothing, so the column vanished on first drag.
 */
/** Markdown/HTML copies are meant to be pasted where a person reads them. */
export const MAX_RICH_COPY_ROWS = 5000;

// A cell value at/above this length opens the detail viewer modal on
// double-click instead of the one-line in-place input.
export const LONG_VALUE_THRESHOLD = 200;

// A staged value that matches the row's stored value isn't an edit at all —
// it's a revert, and the pending edit should be dropped rather than recorded
// (otherwise the save would emit a no-op UPDATE and the cell would stay
// highlighted as changed). Shared by the single-cell edit paths and the
// multi-cell paste batch so both decide this identically.
export function isRevertToOriginal(original, value) {
  if (value === null) return original === null;
  return original !== null && cellToText(original) === value;
}

// Connection-level failures (vs plain SQL errors): the connection this tab
// references is gone, so showing its reconnect screen beats a dead-end error
// string. Matched against the stringified Rust/sqlx error.
export const CONNECTION_ERROR_RE =
  /not connected|pool.*(closed|timed out)|connection (refused|reset|closed|aborted)|broken pipe|io error|network error|server has gone away|terminating connection/i;

export function isConnectionError(err) {
  return CONNECTION_ERROR_RE.test(String(err));
}

// macOS/WKWebView text inputs auto-substitute straight quotes/dashes with
// typographic equivalents as you type, which breaks SQL string literals
// (a query can end up with a curly opening quote paired with a straight
// closing quote, which the parser can't tokenize as a string at all).
export function normalizeSqlText(str) {
  return str
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[–—]/g, '-');
}

/** One field of a restored table tab's cached metadata, or an empty list. */
export function restoredMetaOf(cached, restored, key) {
  const value = restored ? cached?.table?.[key] : null;
  return Array.isArray(value) ? value : [];
}
